import React, { useState } from "react";
import { ArrowRight, Clock, Sparkles, Heart } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { BUSINESS, IMG } from "../lib/constants";
import TrustStrip from "../components/TrustStrip";
import FaqSection from "../components/FaqSection";
import BookingCTA from "../components/BookingCTA";
import SEOHead from "../components/SEOHead";
import Lightbox from "../components/Lightbox";
import BrowComparison from "../components/BrowComparison";

const BrowLaminationPage = () => {
    const { t } = useLanguage();
    const s = t.services.browLamination;
    const [lightboxIndex, setLightboxIndex] = useState(null);
    
    const gallery = [IMG.browLamination1, IMG.browLamination2, IMG.browLamination3, IMG.browLaminationNew];

    const openLightbox = (index) => setLightboxIndex(index);
    const closeLightbox = () => setLightboxIndex(null);
    const nextImage = () => setLightboxIndex((prev) => (prev + 1) % gallery.length);
    const prevImage = () => setLightboxIndex((prev) => (prev - 1 + gallery.length) % gallery.length);

    return (
        <>
            <SEOHead page="browLamination" />
            
            {/* Hero - About page style with image on right */}
            <section
                data-testid="brow-lamination-hero"
                className="bg-[#3D3D24] text-[#FFFFF3] overflow-hidden"
            >
                <div className="max-w-[1400px] mx-auto grid lg:grid-cols-2 gap-0">
                    <div className="order-2 lg:order-1 px-6 md:px-12 lg:px-16 py-16 md:py-24 lg:py-28 flex flex-col justify-center">
                        <div className="font-label text-[11px] text-[#D4A95A] mb-5">{s.overline}</div>
                        <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl leading-[0.98] mb-6">
                            {s.title}
                        </h1>
                        <p className="text-base md:text-lg leading-relaxed opacity-85 max-w-xl mb-8">
                            {s.subtitle}
                        </p>
                        <a
                            data-testid="brow-lamination-hero-book"
                            href="/contact"
                            className="inline-flex items-center gap-2 bg-[#D4A95A] text-[#3D3D24] px-8 py-4 font-label text-xs hover:bg-[#FFFFF3] transition-colors w-fit"
                        >
                            {t.common.bookConsultation}
                            <ArrowRight size={14} />
                        </a>
                    </div>
                    <div className="order-1 lg:order-2 relative">
                        <img
                            src={IMG.browLamination1}
                            alt="Brow Lamination Las Vegas - fluffy brushed-up brows by Wendy Chester at Beauty Ink LV"
                            className="w-full h-full object-cover object-top min-h-[420px] lg:min-h-[560px]"
                        />
                    </div>
                </div>
            </section>

            <TrustStrip variant="dark" />

            {/* Video + Description */}
            <section className="bg-[#FFFFF3] py-24 md:py-32">
                <div className="max-w-[1200px] mx-auto px-6 grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
                    {/* Video */}
                    <div className="overflow-hidden aspect-[4/5] bg-[#F5F2E4]">
                        <video
                            src={IMG.videoEyebrowLamination}
                            autoPlay
                            loop
                            muted
                            playsInline
                            className="w-full h-full object-cover"
                        />
                    </div>
                    
                    {/* Content */}
                    <div>
                        <div className="font-label text-[11px] text-[#D4A95A] mb-5">The Service</div>
                        <p className="font-serif text-2xl md:text-3xl text-[#3D3D24] leading-snug mb-8">
                            {s.description}
                        </p>
                        <div className="border-l-2 border-[#D4A95A] pl-6 mb-10">
                            <div className="font-label text-[10px] text-[#3D3D24]/70 mb-2">Best For</div>
                            <p className="text-[#3D3D24]/85 leading-relaxed">{s.bestFor}</p>
                        </div>
                        
                        {/* At a Glance */}
                        <div className="bg-[#3D3D24] text-[#FFFFF3] p-8">
                            <div className="font-label text-[11px] text-[#D4A95A] mb-6">At a Glance</div>
                            <div className="space-y-5">
                                <div className="flex items-start gap-4 pb-5 border-b border-[#FFFFF3]/15">
                                    <Sparkles size={18} className="text-[#D4A95A] mt-1 shrink-0" />
                                    <div>
                                        <div className="font-label text-[10px] opacity-70">Pricing</div>
                                        <div className="text-base mt-1">{s.pricing}</div>
                                    </div>
                                </div>
                                <div className="flex items-start gap-4 pb-5 border-b border-[#FFFFF3]/15">
                                    <Clock size={18} className="text-[#D4A95A] mt-1 shrink-0" />
                                    <div>
                                        <div className="font-label text-[10px] opacity-70">Duration</div>
                                        <div className="text-base mt-1">{s.duration}</div>
                                    </div>
                                </div>
                                <div className="flex items-start gap-4">
                                    <Heart size={18} className="text-[#D4A95A] mt-1 shrink-0" />
                                    <div>
                                        <div className="font-label text-[10px] opacity-70">Lasts</div>
                                        <div className="text-base mt-1">{s.healing}</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Gallery */}
            <section
                data-testid="brow-lamination-gallery"
                className="bg-[#F5F2E4] py-24 md:py-32"
            >
                <div className="max-w-[1400px] mx-auto px-6">
                    <div className="mb-12">
                        <div className="font-label text-[11px] text-[#D4A95A] mb-5">
                            {t.home.galleryOverline}
                        </div>
                        <h2 className="font-serif text-4xl md:text-5xl text-[#3D3D24] leading-[1.05]">
                            {t.common.gallery} — {s.title}
                        </h2>
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
                        {gallery.map((src, i) => (
                            <div
                                key={i}
                                data-testid={`gallery-browLamination-${i}`}
                                className="overflow-hidden aspect-[4/5] bg-[#F5F2E4] cursor-pointer group"
                                onClick={() => openLightbox(i)}
                            >
                                <img
                                    src={src}
                                    alt={`Brow Lamination Las Vegas results ${i + 1} - Beauty Ink LV`}
                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                />
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Lightbox */}
            {lightboxIndex !== null && (
                <Lightbox
                    images={gallery}
                    currentIndex={lightboxIndex}
                    onClose={closeLightbox}
                    onNext={nextImage}
                    onPrev={prevImage}
                />
            )}

            {/* Brow Comparison */}
            <BrowComparison currentService="brow-lamination" />

            <FaqSection />
            <BookingCTA variant="dark" testid="booking-cta-browLamination" />
        </>
    );
};

export default BrowLaminationPage;
